/**
 * Home Page Component
 *
 * This file implements the main landing page of the application with internationalization support.
 * It demonstrates the use of i18next for multi-language content, React Router's data API for
 * server-side rendering, and responsive design with Tailwind CSS.
 *
 * Key features:
 * - Server-side translation with i18next
 * - Client-side translation with useTranslation hook
 * - SEO-friendly metadata using React Router's meta export
 * - Responsive typography with Tailwind CSS
 */
import type { Route } from "./+types/home";

import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router";

import i18next from "~/core/lib/i18next.server";
import makeServerClient from "~/core/lib/supa-client.server";
import {
  type BackgroundMode,
  StyleGrid,
} from "~/features/home/components/style-grid";
import { HomePageSkeleton } from "~/core/components/skeletons";
import {
  getStyleImageUrlOptimized,
  getStyleImageUrls,
} from "~/features/home/lib/storage-url";
import { getUserGenerations, getVisibleStyles } from "~/features/home/queries";
import { MarqueeBanner } from "~/features/home/components/marquee-banner";
import { PaymentSuccessDialog } from "~/features/payments/components/payment-success-dialog";
import type { HighTrafficStatus } from "~/features/home/schema";
import { DEFAULT_QUEUE_CONFIG } from "~/features/home/lib/queue-config";

/**
 * Meta function for setting page metadata
 *
 * This function generates SEO-friendly metadata for the home page using data from the loader.
 * It sets:
 * - Page title from translated "home.title" key
 * - Meta description from translated "home.subtitle" key
 *
 * The metadata is language-specific based on the user's locale preference.
 *
 * @param data - Data returned from the loader function containing translated title and subtitle
 * @returns Array of metadata objects for the page
 */
export const meta: Route.MetaFunction = ({ data }) => {
  return [
    { title: data?.title },
    { name: "description", content: data?.subtitle },
  ];
};

/**
 * Links function for preloading critical resources
 *
 * Preloads the LCP (Largest Contentful Paint) image to reduce Resource load delay.
 * The first style (kkbeauty) is the LCP element on the home page (display_order=1).
 *
 * Note: Using Supabase Image Transformation with same params as getStyleImageUrlOptimized
 */
export const links: Route.LinksFunction = () => {
  const supabaseUrl = "https://dmminyjolzbmmzeiuyri.supabase.co";
  const lcpImageUrl = `${supabaseUrl}/storage/v1/render/image/public/style-thumbnails/kkbeauty.webp?width=900&height=1200&resize=cover&format=origin`;

  return [
    {
      rel: "preload",
      href: lcpImageUrl,
      as: "image",
      type: "image/webp",
    },
  ];
};

/**
 * Loader function for server-side data fetching
 *
 * This function is executed on the server before rendering the component.
 * It:
 * 1. Extracts the user's locale from the request (via cookies or Accept-Language header)
 * 2. Creates a translation function for that specific locale
 * 3. Fetches visible styles from the database (without prompts)
 * 4. Returns translated strings and styles for rendering
 *
 * This approach ensures that even on first load, users see content in their preferred language,
 * which improves both user experience and SEO (search engines see localized content).
 *
 * @param request - The incoming HTTP request containing locale information
 * @returns Object with translated title, subtitle, and visible styles
 */
export async function loader({ request }: Route.LoaderArgs) {
  // Get Supabase client
  const [supabaseClient] = makeServerClient(request);

  // Helper function for traffic status fetch
  const fetchTrafficStatus = async (): Promise<HighTrafficStatus> => {
    try {
      const { data: trafficData } = (await supabaseClient
        .from("system_status" as any)
        .select("value")
        .eq("key", "high_traffic")
        .single()) as { data: { value: HighTrafficStatus } | null };
      return trafficData?.value || { isHighTraffic: false, activeCount: 0, threshold: DEFAULT_QUEUE_CONFIG.traffic_threshold };
    } catch {
      return { isHighTraffic: false, activeCount: 0, threshold: DEFAULT_QUEUE_CONFIG.traffic_threshold };
    }
  };

  // Parallel fetch: translations, styles, user auth, and traffic status (reduces ~500ms)
  const [t, styles, { data: { user } }, trafficStatus] = await Promise.all([
    i18next.getFixedT(request),
    getVisibleStyles(supabaseClient),
    supabaseClient.auth.getUser(),
    fetchTrafficStatus(),
  ]);

  // Fetch pending generations only for authenticated users
  let pendingStyleIds: string[] = [];
  if (user) {
    try {
      const activeGenerations = await getUserGenerations(
        supabaseClient,
        user.id,
        ["pending", "processing"],
      );
      pendingStyleIds = [
        ...new Set(activeGenerations?.map((g) => g.style_id) || []),
      ];
    } catch {
      // Ignore errors, pending indicators are optional
    }
  }

  // Transform style image URLs from filenames to optimized Supabase Storage URLs
  // Grid images use Image Transformation for smaller file sizes (900x1200)
  const stylesWithUrls = styles.map((style) => ({
    ...style,
    image_url: getStyleImageUrlOptimized(style.image_url, { width: 900, height: 1200 }),
    samples: getStyleImageUrls(style.samples),
  }));

  // Return translated strings and styles for use in both the component and meta function
  return {
    title: t("home.title"),
    subtitle: t("home.subtitle"),
    styles: stylesWithUrls,
    pendingStyleIds,
    isAuthenticated: !!user,
    isHighTraffic: trafficStatus.isHighTraffic,
    activeCount: trafficStatus.activeCount,
  };
}

/**
 * Home page component
 *
 * This is the main landing page component of the application. It displays a simple,
 * centered layout with a headline and subtitle, both internationalized using i18next.
 *
 * Features:
 * - Uses the useTranslation hook for client-side translation
 * - Implements responsive design with Tailwind CSS
 * - Maintains consistent translations between server and client
 * - Displays style grid loaded from database
 *
 * The component is intentionally simple to serve as a starting point for customization.
 * It demonstrates the core patterns used throughout the application:
 * - Internationalization
 * - Responsive design
 * - Clean, semantic HTML structure
 *
 * @returns JSX element representing the home page
 */
export default function Home({ loaderData }: Route.ComponentProps) {
  const [heroTitleSize, setHeroTitleSize] = useState<number>(17.5);
  const navigate = useNavigate();
  const { styles, pendingStyleIds, isAuthenticated, isHighTraffic } = loaderData;

  useEffect(() => {
    const handleScroll = () =>
      setHeroTitleSize(Math.max(11, 17.5 - window.scrollY / 40));
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleStyleSelect = (styleId: BackgroundMode) => {
    // Guest handling: redirect to signup with return URL
    if (!isAuthenticated) {
      navigate(`/auth/signup?redirect=/${styleId}`);
      return;
    }
    navigate(`/${styleId}`);
  };

  return (
    <div className="relative z-10 flex w-full grow flex-col">
      {/* Hero Section */}
      <div className="relative flex min-h-[23vh] w-full flex-col justify-center overflow-hidden border-b border-black/10 px-4 md:min-h-[34vh] md:px-12">
      <h2 className="font-black leading-[0.75] tracking-tighter uppercase text-left whitespace-nowrap -ml-[0.03em]" style={{ fontSize: `${heroTitleSize}vw` }}>
          Pickle
        </h2>
        <div className="mt-[5px] flex w-full flex-row items-end justify-between gap-4 pb-6 md:mt-4">
        <p className="text-[10px] md:text-sm font-medium max-w-[50%] font-mono text-gray-500 uppercase tracking-wide text-left leading-tight">
                    AI Photo Studio<br/> Create stunning visuals<br/> Generate your persona
                </p>
          <div className="flex items-center gap-1.5 md:gap-2">
            <span
              className={`h-1.5 w-1.5 animate-pulse rounded-full md:h-2 md:w-2 ${isHighTraffic ? "bg-red-500" : "bg-green-500"}`}
            ></span>
            <span className="text-right text-xs font-bold tracking-widest uppercase">
              {isHighTraffic ? "High Traffic" : "System Operational"}
            </span>
          </div>
        </div>
      </div>

      {/* Marquee Banner */}
      <MarqueeBanner />

      {/* Style Grid Section */}
      <StyleGrid
        styles={styles}
        onStyleSelect={handleStyleSelect}
        pendingStyleIds={pendingStyleIds}
      />
      <Outlet />

      {/* Payment Success Dialog */}
      <PaymentSuccessDialog />
    </div>
  );
}

/**
 * HydrateFallback - Shows skeleton during SSR hydration
 */
export function HydrateFallback() {
  return <HomePageSkeleton />;
}
